import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { DownloadIcon, ListChecksIcon, MoreVerticalIcon, SearchIcon, Settings2Icon, TagIcon, Trash2Icon, UsersIcon } from 'lucide-react';
import { toast } from '../../components/ui/Toast';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { addNote, bulkDeleteLeads, bulkUpdateStage, deleteLead, updateLeadStage } from '../../services/leadService';
import { usePreferences } from '../../hooks/usePreferences';
import { DEFAULT_LEAD_COLUMNS, DEFAULT_LEADS_PER_PAGE, LEADS_PER_PAGE_OPTIONS } from '../../services/preferencesService';
import { FloatingPanel } from '../../components/shared/FloatingPanel';
import { downloadCsv } from '../../utils/csv';
import { formatDate, formatDateTime, fullName } from '../../utils/format';
import { Lead, LeadNote, NoteColor } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { Table } from '../../components/ui/Table';
import { FloatingMenu } from '../../components/shared/FloatingMenu';
import { SourceBadge } from '../../components/crm/SourceBadge';
import { StageBadge } from '../../components/crm/StageBadge';
import { StageManager } from '../../components/crm/StageManager';
import { SourceManager } from '../../components/crm/SourceManager';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { noteColor } from '../../components/crm/noteColors';
import { NoteDialog } from '../../components/crm/NoteDialog';
import { LeadDrawer } from '../../components/crm/LeadDrawer';
import { dash, resolveLeadContact } from '../../utils/leadFields';

/**
 * Paginación en cliente mientras los datos son de demostración.
 *
 * TODO(Supabase): sustituir por una consulta paginada en servidor
 * (`.range(offset, offset + limit - 1)`) que reciba los mismos filtros,
 * búsqueda y orden, para no descargar todos los leads al navegador.
 * `pageSize` y `currentPage` ya son los únicos valores necesarios.
 */

/**
 * Columnas disponibles en la tabla de leads.
 * TODO(Supabase): guardar la selección por usuario en `user_preferences`.
 */
const COLUMN_OPTIONS: {
  key: string;
  label: string;
}[] = [{
  key: 'name',
  label: 'Nombre'
}, {
  key: 'company',
  label: 'Empresa'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'phone',
  label: 'Teléfono'
}, {
  key: 'instagram',
  label: 'Instagram'
}, {
  key: 'city',
  label: 'Ciudad'
}, {
  key: 'service',
  label: 'Tipo de servicio'
}, {
  key: 'adBudget',
  label: 'Inversión actual'
}, {
  key: 'budget',
  label: 'Presupuesto disponible'
}, {
  key: 'source',
  label: 'Fuente'
}, {
  key: 'campaign',
  label: 'Campaña'
}, {
  key: 'landing',
  label: 'Landing'
}, {
  key: 'form',
  label: 'Formulario'
}, {
  key: 'stage',
  label: 'Estado'
}, {
  key: 'owner',
  label: 'Comercial'
}, {
  key: 'createdAt',
  label: 'Fecha'
}, {
  key: 'lastContact',
  label: 'Último contacto'
}, {
  key: 'note',
  label: 'Última nota'
}];
/** Columnas activas la primera vez, en este orden. */
const DEFAULT_COLUMNS = DEFAULT_LEAD_COLUMNS;
export function Leads() {
  const {
    user,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    leads,
    stages,
    sources,
    forms,
    landings,
    notes,
    isDemo
  } = useWorkspace();
  const members = useWorkspaceMembers();
  const [drawerLead, setDrawerLead] = useState<string | null>(null);
  /** Lead cuya nota se está escribiendo: el editor se abre directamente. */
  const [noteLead, setNoteLead] = useState<Lead | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [confirmTrash, setConfirmTrash] = useState<Lead | null>(null);
  const [confirmBulkTrash, setConfirmBulkTrash] = useState(false);
  const [trashing, setTrashing] = useState(false);
  const [preferences, updatePreferences] = usePreferences();
  const visibleColumns = preferences.leadColumns;
  const setVisibleColumns = (updater: string[] | ((prev: string[]) => string[])) => updatePreferences({
    leadColumns: typeof updater === 'function' ? updater(visibleColumns) : updater
  });
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const [search, setSearch] = useState('');
  const [stageId, setStageId] = useState('');
  const [formId, setFormId] = useState('');
  const [landingId, setLandingId] = useState('');
  const [campaign, setCampaign] = useState('');
  const [owner, setOwner] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkStage, setBulkStage] = useState('');
  const [applying, setApplying] = useState(false);
  const campaigns = useMemo(() => [...new Set(leads.map(l => l.origin.campaign).filter(Boolean))], [leads]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return leads.filter(lead => {
      if (term && ![lead.firstName, lead.lastName, lead.company, lead.email, lead.phone].join(' ').toLowerCase().includes(term)) return false;
      if (stageId && lead.stageId !== stageId) return false;
      if (formId && lead.origin.formId !== formId) return false;
      if (landingId && lead.origin.landingId !== landingId) return false;
      if (campaign && lead.origin.campaign !== campaign) return false;
      if (owner && lead.assignedTo !== owner) return false;
      if (from && lead.createdAt < from) return false;
      if (to && lead.createdAt > `${to}T23:59:59`) return false;
      return true;
    });
  }, [leads, search, stageId, formId, landingId, campaign, owner, from, to]);
  const pageSize = preferences.leadsPerPage || DEFAULT_LEADS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const offset = (currentPage - 1) * pageSize;
  const paged = filtered.slice(offset, offset + pageSize);
  const rangeFrom = filtered.length === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + pageSize, filtered.length);

  /** Números de página a mostrar, con la actual siempre centrada. */
  const pageNumbers = useMemo(() => {
    const max = 5;
    if (totalPages <= max) return Array.from({
      length: totalPages
    }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - max + 1));
    return Array.from({
      length: max
    }, (_, i) => start + i);
  }, [currentPage, totalPages]);
  function stageName(lead: Lead) {
    return stages.find(s => s.id === lead.stageId)?.name ?? 'Nuevo';
  }
  function memberName(id?: string) {
    const member = members.find(m => m.userId === id);
    return member ? fullName(member.firstName, member.lastName) : 'Sin asignar';
  }

  /** Última nota del lead, para la vista previa en la tabla. */
  function lastNote(leadId: string) {
    return notes.filter(n => n.leadId === leadId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  }
  function exportCsv() {
    downloadCsv('leads-ava.csv', ['Nombre', 'Apellidos', 'Empresa', 'Email', 'Teléfono', 'Instagram', 'Ciudad', 'País', 'Servicio solicitado', 'Presupuesto publicidad', 'Presupuesto disponible', 'Formulario', 'Landing', 'Fuente', 'Campaña', 'Estado', 'Comercial', 'Fecha', 'Última nota'], filtered.map(lead => [lead.firstName, lead.lastName, contactOf(lead).company, contactOf(lead).email, contactOf(lead).phone, lead.instagram ?? '', lead.city ?? '', lead.country ?? '', lead.service ?? '', lead.adBudget ?? '', lead.budget ?? '', forms.find(f => f.id === lead.origin.formId)?.name ?? '', landings.find(l => l.id === lead.origin.landingId)?.name ?? '', lead.origin.source, lead.origin.campaign, stageName(lead), memberName(lead.assignedTo), formatDate(lead.createdAt), lastNote(lead.id)?.body ?? '']));
  }

  /** Contacto normalizado del lead, igual que en el embudo y en la ficha. */
  function contactOf(lead: Lead) {
    return resolveLeadContact(lead);
  }

  /** Guarda una nota escrita desde la tabla, sin abrir la ficha. */
  async function saveTableNote(note: {
    title?: string;
    body: string;
    color: NoteColor;
  }) {
    if (!noteLead) return;
    await addNote(noteLead.organizationId, noteLead.id, user?.id ?? 'user', authorName, note.body, note.title, note.color);
    toast.success('Nota guardada');
  }

  /** Menú de acciones rápidas de cada fila o tarjeta. */
  function rowActions(lead: Lead) {
    const phone = contactOf(lead).phone;
    const items = [{
      label: 'Abrir lead',
      onSelect: () => setDrawerLead(lead.id)
    }, {
      label: 'Abrir WhatsApp',
      onSelect: () => window.open(`https://wa.me/${phone.replace(/\D/g, '')}`, '_blank', 'noopener')
    }, {
      label: 'Copiar teléfono',
      onSelect: () => navigator.clipboard?.writeText(phone)
    }];
    if (hasPermission('leads.edit')) {
      items.splice(1, 0, {
        label: 'Escribir nota',
        onSelect: () => setNoteLead(lead)
      });
    }
    if (hasPermission('leads.delete')) {
      items.push({
        label: 'Mover a papelera',
        destructive: true,
        onSelect: () => setConfirmTrash(lead)
      } as never);
    }
    return items;
  }
  async function applyBulk() {
    if (!bulkStage || selected.length === 0) return;
    setApplying(true);
    try {
      await bulkUpdateStage(selected, bulkStage, authorName);
      setSelected([]);
      setBulkStage('');
    } catch (error) {
      toast.error('No se ha podido cambiar el estado', {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setApplying(false);
    }
  }

  /**
   * Mueve a la papelera un lead o la selección completa. El error real de
   * Supabase se muestra en pantalla y en consola: nunca se simula el borrado.
   */
  async function moveToTrash(ids: string[]) {
    setTrashing(true);
    try {
      if (ids.length === 1) await deleteLead(ids[0], authorName);else await bulkDeleteLeads(ids, authorName);
      toast.success(ids.length === 1 ? 'Lead movido a la papelera' : 'Leads movidos a la papelera', {
        description: 'Puedes restaurarlo desde Papelera.'
      });
      setConfirmTrash(null);
      setConfirmBulkTrash(false);
      setSelected(prev => prev.filter(id => !ids.includes(id)));
      if (drawerLead && ids.includes(drawerLead)) setDrawerLead(null);
    } catch (error) {
      toast.error('No se ha podido mover a la papelera', {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setTrashing(false);
    }
  }
  if (leads.length === 0) {
    return <>
        <PageHeader title="Leads" description="Contactos recibidos por tu empresa." data-id="element-1663" data-mp="2f1hs9dljz5di" />
        <EmptyState icon={<UsersIcon className="h-5 w-5" data-id="element-1665" data-mp="2k0b8hds85ahd" />} title="Todavía no tienes contactos" description="Publica una landing o comparte un formulario para comenzar a recibir leads." action={<Link to="/app/landings" data-id="element-1666" data-mp="255esgee6bitq">
              <Button data-id="element-1667" data-mp="2a2wrnd2e2joj">Ir a landings</Button>
            </Link>} data-id="element-1664" data-mp="2eznhut7wqvno" />
      </>;
  }
  return <>
      <PageHeader title="Leads" description={`${filtered.length} contactos que cumplen los filtros.`} actions={<>
            {isDemo && <DemoBadge className="self-center" data-id="element-1669" data-mp="210xcyreujror" />}
            <Button variant="secondary" leftIcon={<DownloadIcon className="h-4 w-4" data-id="element-1671" data-mp="3qg3njutntxxc" />} onClick={exportCsv} data-id="element-1670" data-mp="213jspebw5di2">
              Exportar CSV
            </Button>
            <Button variant="secondary" leftIcon={<Settings2Icon className="h-4 w-4" data-id="element-1673" data-mp="3vdndvt3mu15n" />} onClick={() => setColumnsOpen(true)} data-id="element-1672" data-mp="3veooxxknennd">
              Personalizar columnas
            </Button>
            <Button variant="secondary" leftIcon={<ListChecksIcon className="h-4 w-4" data-id="element-1675" data-mp="3oy6g1dvlujsm" />} onClick={() => setStagesOpen(true)} data-id="element-1674" data-mp="3oz83khtt4yr8">
              Gestionar estados
            </Button>
            <Button variant="secondary" leftIcon={<TagIcon className="h-4 w-4" data-id="element-1677" data-mp="3f2lk9xh4qfrb" />} onClick={() => setSourcesOpen(true)} data-id="element-1676" data-mp="3f1jyaxyks8ut">
              Gestionar fuentes
            </Button>
          </>} data-id="element-1668" data-mp="1w2m2qfkn3f60" />

      {/* Filtros */}
      <div className="mb-5 rounded-2xl border border-border bg-background p-5 shadow-soft" data-id="element-1678" data-mp="bk1r8sqmn5vg">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1679" data-mp="6tl9iwfrwy3a">
          {/* Buscador: la lupa va absolutamente posicionada y el campo
              reserva su espacio con padding, para que nunca se solapen. */}
          <div className="relative" data-id="element-1680" data-mp="zafi4fxh6yog">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-1681" data-mp="zbgy8ytcmbd1" />
            <input id="buscar" type="search" aria-label="Buscar leads" placeholder="Buscar por nombre, empresa o email" value={search} onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }} className="h-11 w-full rounded-xl border border-border bg-background pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1682" data-mp="11v3qi7iwo9o0" />
          </div>
          <Select id="f-estado" placeholder="Todos los estados" options={stages.map(s => ({
          value: s.id,
          label: s.name
        }))} value={stageId} onChange={e => setStageId(e.target.value)} data-id="element-1683" data-mp="1krdepnniuglq" />
          <Select id="f-form" placeholder="Todos los formularios" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1684" data-mp="1zickys0vwt25" />
          <Select id="f-landing" placeholder="Todas las landings" options={landings.map(l => ({
          value: l.id,
          label: l.name
        }))} value={landingId} onChange={e => setLandingId(e.target.value)} data-id="element-1685" data-mp="28ir7tvthyg7r" />
          <Select id="f-campana" placeholder="Todas las campañas" options={campaigns.map(c => ({
          value: c,
          label: c
        }))} value={campaign} onChange={e => setCampaign(e.target.value)} data-id="element-1686" data-mp="319lv3vutzr08" />
          <Select id="f-comercial" placeholder="Todos los comerciales" options={members.map(m => ({
          value: m.userId,
          label: fullName(m.firstName, m.lastName)
        }))} value={owner} onChange={e => setOwner(e.target.value)} data-id="element-1687" data-mp="3azoqg2pfdh1q" />
          <Input id="f-desde" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} data-id="element-1688" data-mp="2icayyk4hxdp0" />
          <Input id="f-hasta" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} data-id="element-1689" data-mp="2ramp6l1ga9gx" />
        </div>
      </div>

      {/* Acciones en lote */}
      {selected.length > 0 && hasPermission('leads.edit') && <div className="mb-4 flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center" data-id="element-1690" data-mp="6kxb4a8kmrpa">
          <p className="text-sm font-medium text-foreground" data-id="element-1691" data-mp="ezuvvug74hqe">
            {selected.length} leads seleccionados
          </p>
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:justify-end" data-id="element-1692" data-mp="j0wzenh5bfy6">
            <Select id="bulk" placeholder="Cambiar estado a…" options={stages.map(s => ({
          value: s.id,
          label: s.name
        }))} value={bulkStage} onChange={e => setBulkStage(e.target.value)} data-id="element-1693" data-mp="552yaqmm5uvm" />
            <Button onClick={applyBulk} loading={applying} disabled={!bulkStage} data-id="element-1694" data-mp="xu0dgkvj3bk5">
              Aplicar
            </Button>
            {hasPermission('leads.delete') && <Button variant="destructive" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-1696" data-mp="o000jwu0920v" />} onClick={() => setConfirmBulkTrash(true)} data-id="element-1695" data-mp="o12qa1flcyf7">
                Eliminar
              </Button>}
            <Button variant="ghost" onClick={() => setSelected([])} data-id="element-1697" data-mp="1gqi40yyn55et">
              Cancelar
            </Button>
          </div>
        </div>}

      {/* Tabla escritorio */}
      <div className="hidden lg:block" data-id="element-1698" data-mp="1vdgryjyahhos">
        <Table data={paged} rowKey={lead => lead.id} selectable={hasPermission('leads.edit')} selectedKeys={selected} onSelectionChange={setSelected} emptyMessage="Ningún lead cumple estos filtros." columns={[{
        key: 'name',
        header: 'Nombre',
        sortable: true,
        render: lead => <button type="button" onClick={() => setDrawerLead(lead.id)} className="text-left font-medium text-foreground hover:text-primary" data-id="element-1700" data-mp="1hcnx1y42skl5">
                  {dash(contactOf(lead).name)}
                </button>
      }, {
        key: 'company',
        header: 'Empresa',
        render: lead => dash(contactOf(lead).company)
      }, {
        key: 'phone',
        header: 'Teléfono',
        render: lead => dash(contactOf(lead).phone)
      }, {
        key: 'email',
        header: 'Email',
        render: lead => dash(contactOf(lead).email)
      }, {
        key: 'instagram',
        header: 'Instagram',
        render: lead => lead.instagram || '—'
      }, {
        key: 'city',
        header: 'Ciudad',
        render: lead => lead.city || '—'
      }, {
        key: 'service',
        header: 'Servicio solicitado',
        render: lead => lead.service || '—'
      }, {
        key: 'adBudget',
        header: 'Presup. publicidad',
        render: lead => lead.adBudget || '—'
      }, {
        key: 'budget',
        header: 'Presup. disponible',
        render: lead => lead.budget || '—'
      }, {
        key: 'source',
        header: 'Fuente',
        render: lead => <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1701" data-mp="2lmpouvmzmnf5" />
      }, {
        key: 'stage',
        header: 'Estado',
        render: lead => <StageBadge stage={stages.find(s => s.id === lead.stageId)} stages={hasPermission('leads.edit') ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-1702" data-mp="2ql982oa75l3t" />
      }, {
        key: 'owner',
        header: 'Comercial',
        render: lead => memberName(lead.assignedTo)
      }, {
        key: 'campaign',
        header: 'Campaña',
        render: lead => lead.origin.campaign || '—'
      }, {
        key: 'landing',
        header: 'Landing',
        render: lead => landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'
      }, {
        key: 'form',
        header: 'Formulario',
        render: lead => forms.find(f => f.id === lead.origin.formId)?.name ?? '—'
      }, {
        key: 'createdAt',
        header: 'Fecha',
        sortable: true,
        render: lead => formatDate(lead.createdAt)
      }, {
        key: 'lastContact',
        header: 'Último contacto',
        render: lead => lead.lastContactAt ? formatDate(lead.lastContactAt) : 'Sin contactar'
      }, {
        key: 'note',
        header: 'Última nota',
        render: lead => <NotePreview note={lastNote(lead.id)} onWrite={hasPermission('leads.edit') ? () => setNoteLead(lead) : undefined} data-id="element-1703" data-mp="1k01j9z58l3sn" />
      }, {
        key: 'actions',
        header: '',
        render: lead => <FloatingMenu align="right" label="Acciones del contacto" trigger={<span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface" data-id="element-1705" data-mp="2clgfvgmb5gne">
                      <MoreVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1706" data-mp="2hk1ejblwvs9q" />
                    </span>} items={rowActions(lead)} data-id="element-1704" data-mp="1t1itx1zvkd9y" />
      }].filter(column => column.key === 'actions' || visibleColumns.includes(column.key))} data-id="element-1699" data-mp="1vcfigjwx9396" />
      </div>

      {/* Tarjetas móvil */}
      <ul className="grid gap-3 lg:hidden" data-id="element-1707" data-mp="1tuymraxkcj1i">
        {paged.map(lead => {
        const note = lastNote(lead.id);
        const expanded = expandedCard === lead.id;
        return <li key={lead.id} className="rounded-2xl border border-border bg-background p-5 shadow-soft" data-id="element-1708" data-mp="1jx2celcg9s4g">
              <div className="flex items-start justify-between gap-3" data-id="element-1709" data-mp="1ovvs8kf7lv7s">
                <div className="min-w-0" data-id="element-1710" data-mp="1ou2igw9auuvk">
                  <p className="truncate font-semibold text-foreground" data-id="element-1711" data-mp="1aw4801y3nilc">
                    {dash(contactOf(lead).name)}
                  </p>
                  <p className="truncate text-sm text-muted-foreground" data-id="element-1712" data-mp="1funzsizdjolm">
                    {contactOf(lead).company}
                  </p>
                </div>
                <FloatingMenu align="right" label="Acciones del contacto" trigger={<span className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground" data-id="element-1714" data-mp="151vxt1f74yfc">
                      <MoreVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1715" data-mp="1a074j3a72stc" />
                    </span>} items={rowActions(lead)} data-id="element-1713" data-mp="1j2q5fhuf6vcy" />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1716" data-mp="1xsnz2db4cd9j">
                <StageBadge stage={stages.find(s => s.id === lead.stageId)} stages={hasPermission('leads.edit') ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-1717" data-mp="21qv6ghjp0282" />
                <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1718" data-mp="1syjrpsxxy37x" />
              </div>

              {note && <div className={`mt-3 rounded-lg border-l-4 px-3 py-2 ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-1719" data-mp="2gonl77hf0ml7">
                  <p className="line-clamp-2 text-xs leading-snug text-foreground" data-id="element-1720" data-mp="2lhyt7bxyr9ep">
                    {note.body}
                  </p>
                </div>}

              {expanded && <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs text-muted-foreground" data-id="element-1721" data-mp="2qsufcpgvuip4">
                  <div className="truncate" data-id="element-1722" data-mp="34mv54tpuwvdc">{dash(contactOf(lead).email)}</div>
                  <div className="truncate" data-id="element-1723" data-mp="2zm7iek8nv0ox">{dash(contactOf(lead).phone)}</div>
                  <div className="truncate" data-id="element-1724" data-mp="2bx4q8igehpg6">{lead.city || '—'}</div>
                  <div className="truncate" data-id="element-1725" data-mp="27urn81t1g5n3">{lead.service || '—'}</div>
                  <div className="truncate" data-id="element-1726" data-mp="27wkrxbk3cfsg">{lead.budget || '—'}</div>
                  <div className="truncate" data-id="element-1727" data-mp="2lv2hzfxgy4dj">{formatDate(lead.createdAt)}</div>
                </dl>}

              <div className="mt-3 flex gap-2" data-id="element-1728" data-mp="2guemagyaozcz">
                <Button variant="ghost" size="sm" className="min-h-[44px] flex-1" onClick={() => setExpandedCard(expanded ? null : lead.id)} data-id="element-1729" data-mp="1420j6f23gvb">
                  {expanded ? 'Ver menos' : 'Ver más'}
                </Button>
                <Button variant="secondary" size="sm" className="min-h-[44px] flex-1" onClick={() => setDrawerLead(lead.id)} data-id="element-1730" data-mp="46erwsxh826k">
                  Abrir ficha
                </Button>
              </div>
            </li>;
      })}
      </ul>

      {/* Paginación */}
      <nav className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" aria-label="Paginación de leads" data-id="element-1731" data-mp="d4idtqcdj0yw">
        <p className="order-2 text-sm text-muted-foreground lg:order-1" data-id="element-1732" data-mp="3gnk1jn9w4h0k">
          <span className="hidden sm:inline" data-id="element-1733" data-mp="3gl7b0lq2gybw">
            Mostrando {rangeFrom}–{rangeTo} de {filtered.length} contactos
          </span>
          <span className="sm:hidden" data-id="element-1734" data-mp="3qky3yiyzb59v">
            {rangeFrom}–{rangeTo} de {filtered.length}
          </span>
        </p>

        <div className="order-3 flex flex-wrap items-center justify-center gap-2 lg:order-2" data-id="element-1735" data-mp="3vdixdjdz3gcv">
          <Button variant="secondary" size="sm" className="min-h-[44px]" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} data-id="element-1736" data-mp="2xvr9pw6k51uf">
            Anterior
          </Button>
          {totalPages > 1 && <ul className="hidden items-center gap-1 sm:flex" data-id="element-1737" data-mp="2vbm76mxi6osj">
              {pageNumbers.map(number => <li key={number} data-id="element-1738" data-mp="2qecmfkl271b1">
                  <button type="button" aria-label={`Ir a la página ${number}`} aria-current={number === currentPage ? 'page' : undefined} onClick={() => setPage(number)} className={`min-h-[44px] min-w-[44px] rounded-xl px-3 text-sm font-medium transition-colors ${number === currentPage ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-surface hover:text-foreground'}`} data-id="element-1739" data-mp="2qayl8fo4sy56">
                    {number}
                  </button>
                </li>)}
            </ul>}
          <span className="text-sm text-muted-foreground sm:hidden" data-id="element-1740" data-mp="22nqcfnrhv6ye">
            {currentPage} / {totalPages}
          </span>
          <Button variant="secondary" size="sm" className="min-h-[44px]" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} data-id="element-1741" data-mp="1sp9hygtcka7l">
            Siguiente
          </Button>
        </div>

        {/* Contactos por página */}
        <div className="order-1 flex items-center gap-2 lg:order-3" data-id="element-1742" data-mp="2dfmvtsl1gq3z">
          <label htmlFor="leads-per-page" className="whitespace-nowrap text-sm text-muted-foreground" data-id="element-1743" data-mp="2idy5a27f8ylo">
            Contactos por página
          </label>
          <select id="leads-per-page" value={pageSize} onChange={e => {
          updatePreferences({
            leadsPerPage: Number(e.target.value)
          });
          setPage(1);
        }} className="h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1744" data-mp="24lrrunvnl5px">
            {LEADS_PER_PAGE_OPTIONS.map(option => <option key={option} value={option} data-id="element-1745" data-mp="1lp8okf7j6c1a">
                {option}
              </option>)}
          </select>
        </div>
      </nav>

      <LeadDrawer leadId={drawerLead} onClose={() => setDrawerLead(null)} data-id="element-1746" data-mp="3g0ngn2o0knk7" />

      {/* Editor de nota directo desde la tabla: sin abrir la ficha. */}
      <NoteDialog open={Boolean(noteLead)} leadName={noteLead ? dash(contactOf(noteLead).name) : ''} onClose={() => setNoteLead(null)} onSave={saveTableNote} data-id="element-1747" data-mp="3k1z5h5z2d4rc" />

      <StageManager open={stagesOpen} onClose={() => setStagesOpen(false)} organizationId={organizationId} stages={stages} data-id="element-1748" data-mp="14emovysvjv8b" />
      <SourceManager open={sourcesOpen} onClose={() => setSourcesOpen(false)} organizationId={organizationId} sources={sources} data-id="element-1749" data-mp="1i6t0d7l31e1f" />

      <ConfirmDialog open={Boolean(confirmTrash)} title="Mover a la papelera" description="El lead saldrá de Leads y del embudo, pero conservará sus notas, respuestas e historial. Podrás restaurarlo desde la Papelera." confirmLabel="Mover a la papelera" loading={trashing} onCancel={() => setConfirmTrash(null)} onConfirm={() => {
      if (confirmTrash) void moveToTrash([confirmTrash.id]);
    }} data-id="element-1750" data-mp="1dcot0n7wn3zt" />

      <ConfirmDialog open={confirmBulkTrash} title={`Mover ${selected.length} leads a la papelera`} description="Saldrán de Leads y del embudo, pero conservarán sus notas, respuestas e historial. Podrás restaurarlos desde la Papelera." confirmLabel="Mover a la papelera" loading={trashing} onCancel={() => setConfirmBulkTrash(false)} onConfirm={() => {
      if (selected.length > 0) void moveToTrash(selected);
    }} data-id="element-1751" data-mp="1w36gac9yx7ds" />

      {/* Panel flotante: se puede mover para no tapar la tabla y los cambios
          se reflejan al instante detrás. */}
      <FloatingPanel open={columnsOpen} title="Personalizar columnas" description="Los cambios se aplican en la tabla al instante." position={preferences.columnPanelPosition} onPositionChange={position => updatePreferences({
      columnPanelPosition: position
    })} onClose={() => setColumnsOpen(false)} footer={<div className="flex flex-wrap gap-2" data-id="element-1753" data-mp="2uj7z2i3ck028">
            <Button size="sm" variant="ghost" onClick={() => setVisibleColumns(COLUMN_OPTIONS.map(c => c.key))} data-id="element-1754" data-mp="2zh01w75j56l7">
              Mostrar todas
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setVisibleColumns([...DEFAULT_COLUMNS])} data-id="element-1755" data-mp="33k64n4tfofmx">
              Restaurar
            </Button>
            <Button size="sm" onClick={() => setColumnsOpen(false)} data-id="element-1756" data-mp="wu2s23xtmnm8">
              Listo
            </Button>
          </div>} data-id="element-1752" data-mp="2fu21szo2kjwz">
        <ul className="grid gap-2" data-id="element-1757" data-mp="ru6ou6wy707q">
          {COLUMN_OPTIONS.map(column => <li key={column.key} data-id="element-1758" data-mp="ntd1pfrbxpfw">
              <Checkbox id={`col-${column.key}`} label={column.label} checked={visibleColumns.includes(column.key)} onChange={e => setVisibleColumns(prev => e.target.checked ? [...prev, column.key] : prev.filter(k => k !== column.key))} data-id="element-1759" data-mp="nuo8zpjzhfbr" />
            </li>)}
        </ul>
      </FloatingPanel>
    </>;
}

/**
 * Vista previa de la nota más reciente: dos líneas como máximo y la nota
 * completa al pasar el cursor o al pulsar en móvil.
 */
function NotePreview({
  note,
  onWrite
}: {
  note?: LeadNote;
  onWrite?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);

  /**
   * El contenido completo se pinta con un portal en `document.body` y posición
   * fija, así nunca queda recortado dentro de la fila ni por el scroll de la
   * tabla. Si no cabe debajo, se coloca encima.
   */
  function show() {
    const box = anchor.current?.getBoundingClientRect();
    if (!box) return;
    const width = 288;
    const below = window.innerHeight - box.bottom > 200;
    setPos({
      top: below ? box.bottom + 8 : Math.max(8, box.top - 208),
      left: Math.min(Math.max(8, box.left), window.innerWidth - width - 8)
    });
    setOpen(true);
  }
  // Sin notas: el propio hueco abre el editor directo.
  if (!note) return onWrite ? <button type="button" onClick={event => {
    event.stopPropagation();
    onWrite();
  }} className="text-left text-xs font-medium text-muted-foreground hover:text-primary" data-id="element-1760" data-mp="1ckut5est06nq">
        Escribir nota
      </button> : <span className="text-muted-foreground" data-id="element-1761" data-mp="14woro8km8su">Sin notas</span>;
  const style = noteColor(note.color);
  const preview = note.body.length > 90 ? `${note.body.slice(0, 90).trim()}…` : note.body;
  return <span ref={anchor} className="block w-56" onMouseEnter={show} onMouseLeave={() => setOpen(false)} data-id="element-1762" data-mp="3trtsrzad9bwq">
      <button type="button" onClick={e => {
      e.stopPropagation();
      if (open) setOpen(false);else show();
    }} className={`flex w-full items-start gap-2 rounded-lg border-l-4 px-2.5 py-2 text-left ${style.bg} ${style.border}`} data-id="element-1763" data-mp="3oubf5vlem8ie">
        <span className={`mt-1 h-2 w-2 flex-none rounded-full ${style.dot}`} aria-hidden="true" data-id="element-1764" data-mp="2n3j5jwdjdz1" />
        <span className="min-w-0" data-id="element-1765" data-mp="3b2erc3qrqkrt">
          {note.title && <span className="block truncate text-xs font-semibold text-foreground" data-id="element-1766" data-mp="363k8j1ssww7p">
              {note.title}
            </span>}
          <span className="line-clamp-2 text-xs leading-snug text-foreground/80" data-id="element-1767" data-mp="3ksq5ej462acf">
            {preview}
          </span>
        </span>
      </button>
      {open && pos && createPortal(<span style={{
      top: pos.top,
      left: pos.left
    }} className={`fixed z-[70] block max-h-52 w-72 overflow-auto rounded-xl border border-border border-l-4 p-3 text-xs leading-relaxed text-foreground shadow-xl ${style.bg} ${style.border}`} data-id="element-1768" data-mp="2sauf4cay0p0k">
            <span className="mb-1 block text-[11px] font-medium text-muted-foreground" data-id="element-1769" data-mp="1i2bryjw17ffi">
              {note.authorName} · {formatDateTime(note.createdAt)}
            </span>
            {note.body}
            {onWrite && <button type="button" onMouseDown={event => {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        onWrite();
      }} className="mt-2 block text-[11px] font-semibold text-primary hover:underline" data-id="element-1770" data-mp="184e4hylzxmny">
                Escribir nota
              </button>}
          </span>, document.body)}
    </span>;
}
function useWorkspaceMembers() {
  const {
    members
  } = useAuth();
  return members;
}